'use client'

import type { ReactNode } from 'react'

type DelmanBottomSheetProps = {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  primaryLabel: string
  onPrimary: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
  secondaryLabel?: string
}

export function DelmanBottomSheet({
  open,
  title,
  onClose,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel = 'Batal',
}: DelmanBottomSheetProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center" role="dialog" aria-modal="true" aria-labelledby="delman-sheet-title">
      <button
        type="button"
        className="absolute inset-0 bg-black/45 sheet-backdrop"
        onClick={onClose}
        aria-label="Tutup dialog"
      />
      <div className="relative w-full max-w-md mx-auto rounded-t-2xl bg-surface-card border border-outline-variant border-b-0 shadow-[0_-8px_32px_rgba(0,0,0,0.12)] overflow-hidden sheet-panel">
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-outline-variant" aria-hidden />
        <div className="px-gutter-tablet pt-4 pb-3 border-b border-surface-variant">
          <h2 id="delman-sheet-title" className="text-headline-md text-primary">
            {title}
          </h2>
        </div>
        <div className="px-gutter-tablet py-4">{children}</div>
        <div className="px-gutter-tablet pb-[max(1rem,env(safe-area-inset-bottom))] pt-0 flex flex-col gap-2">
          <button
            type="button"
            className="btn-primary w-full min-h-[52px] btn-tactile disabled:opacity-50"
            disabled={primaryDisabled || primaryLoading}
            onClick={onPrimary}
          >
            {primaryLoading ? 'Memproses…' : primaryLabel}
          </button>
          <button type="button" className="btn-outline w-full min-h-[44px]" onClick={onClose}>
            {secondaryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function SummaryRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-surface-variant last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className={`text-body-sm font-semibold text-right ${highlight ? 'text-primary' : 'text-on-surface'}`}>
        {value}
      </span>
    </div>
  )
}

export function DelmanSheetSummary({ rows }: { rows: { label: string; value: string; highlight?: boolean }[] }) {
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container-low px-3">
      {rows.map((row) => (
        <SummaryRow key={row.label} {...row} />
      ))}
    </div>
  )
}
